import type { ToastPosition } from "@lib/config/toastConfig";
import { useToastStore } from "@lib/stores/toast";
import { cn } from "@lib/utils/cn";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastCard } from "./ToastCard";

const POSITION_CLASSES: Record<ToastPosition, string> = {
  "top-left": "toast-position toast-top-left",
  "top-right": "toast-position toast-top-right",
  "top-center": "toast-position toast-top-center",
  "bottom-left": "toast-position toast-bottom-left",
  "bottom-right": "toast-position toast-bottom-right",
  "bottom-center": "toast-position toast-bottom-center",
};

/** How many toasts stay visible when the stack is collapsed — the rest sit in
 *  a silent backlog behind the "+N autres" pill until they expire. */
const VISIBLE_WINDOW = 5;

export function ToastContainer({ position }: { position: ToastPosition }) {
  // Select the raw array (stable reference) — filtering in the selector would
  // create a new array every render and cause an infinite re-render loop.
  const allToasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  const [expanded, setExpanded] = useState(false);

  const toasts = useMemo(
    () => allToasts.filter((t) => t.position === position),
    [allToasts, position],
  );

  // Collapsed: keep the 5 newest visible; older toasts run out their timers
  // silently behind the pill. The depth transform is recomputed over the
  // visible window so the visible stack always reads as one coherent unit.
  const hiddenCount = Math.max(toasts.length - VISIBLE_WINDOW, 0);
  const visibleToasts = expanded ? toasts : hiddenCount > 0 ? toasts.slice(hiddenCount) : toasts;

  // Escape dismisses the most recent toast
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && toasts.length > 0) {
        const last = toasts[toasts.length - 1];
        removeToast(last.id);
      }
    },
    [toasts, removeToast],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className={cn(POSITION_CLASSES[position], expanded && "toast-stack-expanded")}
      role="region"
      aria-label="Notifications"
    >
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="toast-more-pill"
          title={`Afficher ${hiddenCount} autres notifications`}
          aria-expanded="false"
          onClick={() => setExpanded(true)}
        >
          +{hiddenCount} autres
        </button>
      )}
      {visibleToasts.map((toast, index) => (
        <div
          key={toast.id}
          style={
            {
              // Depth 0 = newest (front, full size) — older toasts recede
              // behind it. Inverted from the array index so the most recent
              // toast is the most prominent.
              "--depth": Math.min(visibleToasts.length - 1 - index, 2),
            } as React.CSSProperties
          }
        >
          <ToastCard toast={toast} />
        </div>
      ))}
    </div>
  );
}
